/**
 * INVI HQ — Health & reliability view. Surfaces the proactive "what's at risk /
 * what's silently broken" signals: per-dealer-group health scorecard, per-tool
 * error rate, disengaged users, and vendor-feed freshness. Fetches /insights/health
 * for the window + dealer group passed by the parent Insights page.
 */
import React, { useEffect, useState } from "react"

import { THEME } from "../../config/theme"
import {
  InsightsHealth,
  InsightsRange,
  fetchInsightsHealth,
  sendDigestPreview,
} from "../../api/insights"

interface Props {
  range: InsightsRange
  dealerGroupId: number | null
}

const STATUS_COLOR: Record<string, string> = {
  healthy: THEME.colors.semantic.success,
  watch: THEME.colors.semantic.warning,
  at_risk: THEME.colors.semantic.danger,
}

const STATUS_LABEL: Record<string, string> = {
  healthy: "Healthy",
  watch: "Watch",
  at_risk: "At risk",
}

function InsightsHealthView({ range, dealerGroupId }: Props) {
  const [health, setHealth] = useState<InsightsHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [digestMsg, setDigestMsg] = useState<string | null>(null)
  const [digestSending, setDigestSending] = useState(false)

  const previewDigest = async () => {
    setDigestSending(true)
    setDigestMsg(null)
    try {
      const res = await sendDigestPreview()
      setDigestMsg(res.sent ? `Sent to ${res.recipient}` : "Send failed — check email config")
    } catch (e) {
      setDigestMsg(e instanceof Error ? e.message : "Send failed")
    } finally {
      setDigestSending(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await fetchInsightsHealth(range, dealerGroupId)
        if (!cancelled) setHealth(data)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load health")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [range, dealerGroupId])

  if (loading) return <Hint text="Loading health…" />
  if (error) return <Hint text={error} danger />
  if (!health) return <Hint text="No health data." />

  return (
    <>
      {/* Scorecard — the cross-group triage list, worst first */}
      <div style={{ ...panelStyle, marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <h2 style={titleStyle}>
            Dealer-group health{" "}
            <span style={subtitleStyle}>— who needs attention, worst first</span>
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {digestMsg && (
              <span style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary }}>
                {digestMsg}
              </span>
            )}
            <button
              type="button"
              onClick={previewDigest}
              disabled={digestSending}
              style={{
                padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
                fontSize: "13px",
                fontWeight: 500,
                color: THEME.colors.navy,
                background: THEME.colors.neutral.light.surface,
                border: `1px solid ${THEME.colors.silver}`,
                borderRadius: THEME.borderRadius.md,
                cursor: digestSending ? "default" : "pointer",
                fontFamily: THEME.fonts.primary,
                opacity: digestSending ? 0.6 : 1,
              }}
            >
              {digestSending ? "Sending…" : "Email me this digest"}
            </button>
          </div>
        </div>
        {health.scorecard.length === 0 ? (
          <Hint text="No dealer groups." />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                {["Dealer group", "Health", "Active users", "Error rate", "Stale feeds"].map((h, i) => (
                  <th key={h} style={{ ...thStyle, textAlign: i >= 2 ? "right" : "left" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {health.scorecard.map((c) => (
                <tr key={c.dealer_group_id}>
                  <td style={tdStyle}>{c.dealer_group}</td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 700, color: STATUS_COLOR[c.status], marginRight: "8px" }}>
                      {c.score}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: STATUS_COLOR[c.status],
                        background: `${STATUS_COLOR[c.status]}1A`,
                        padding: "2px 8px",
                        borderRadius: THEME.borderRadius.pill,
                      }}
                    >
                      {STATUS_LABEL[c.status] ?? c.status}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{c.active_users}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{(c.error_rate * 100).toFixed(0)}%</td>
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "right",
                      color: c.stale_feeds ? THEME.colors.semantic.danger : undefined,
                      fontWeight: c.stale_feeds ? 600 : 400,
                    }}
                  >
                    {c.stale_feeds}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
        {/* Error rate by tool */}
        <div style={panelStyle}>
          <h2 style={titleStyle}>Tool reliability</h2>
          {health.error_rates.every((r) => r.uses === 0) ? (
            <Hint text="No runs in this window." />
          ) : (
            health.error_rates.map((r) => (
              <div key={r.tool} style={rowStyle}>
                <span>{r.label}</span>
                <span>
                  <span
                    style={{
                      fontWeight: 600,
                      color: r.rate > 0 ? THEME.colors.semantic.danger : THEME.colors.neutral.light.textPrimary,
                    }}
                  >
                    {(r.rate * 100).toFixed(0)}%
                  </span>
                  <span style={{ color: THEME.colors.neutral.light.textSecondary, fontSize: "12px" }}>
                    {" "}({r.errors}/{r.uses})
                  </span>
                </span>
              </div>
            ))
          )}
        </div>

        {/* Vendor feed freshness */}
        <div style={panelStyle}>
          <h2 style={titleStyle}>Vendor feed freshness</h2>
          {health.ingestion_freshness.length === 0 ? (
            <Hint text="No vendor feeds configured." />
          ) : (
            health.ingestion_freshness.map((f, idx) => (
              <div key={idx} style={rowStyle}>
                <span>
                  {f.vendor_name}
                  {f.dealer_group && (
                    <span style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary }}>
                      {" "}· {f.dealer_group}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: f.is_stale ? THEME.colors.semantic.danger : THEME.colors.semantic.success,
                  }}
                >
                  {f.is_stale
                    ? "Stale"
                    : f.last_success_at
                    ? `Fresh · ${new Date(f.last_success_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                    : "Fresh"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Disengaged users */}
      <div style={panelStyle}>
        <h2 style={titleStyle}>
          Disengaged users{" "}
          <span style={subtitleStyle}>— gone quiet, a churn early-warning</span>
        </h2>
        {health.disengaged_users.length === 0 ? (
          <Hint text="Everyone's been active recently. 🎉" />
        ) : (
          health.disengaged_users.map((u) => (
            <div key={u.user_id} style={rowStyle}>
              <span>
                <span style={{ fontWeight: 500 }}>{u.name}</span>
                {u.dealer_group && (
                  <span style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary }}>
                    {" "}· {u.dealer_group}
                  </span>
                )}
              </span>
              <span style={{ fontSize: "13px", color: THEME.colors.semantic.warning, fontWeight: 600 }}>
                {u.days_inactive == null ? "Never signed in" : `${u.days_inactive}d inactive`}
              </span>
            </div>
          ))
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Local styles + tiny helper
// ---------------------------------------------------------------------------

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.lg,
  padding: "20px",
}

const titleStyle: React.CSSProperties = {
  margin: "0 0 16px 0",
  fontSize: "15px",
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const subtitleStyle: React.CSSProperties = {
  fontWeight: 400,
  fontSize: "12px",
  color: THEME.colors.neutral.light.textSecondary,
}

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "14px",
}

const thStyle: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: "11px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  borderBottom: `1px solid ${THEME.colors.silver}`,
}

const tdStyle: React.CSSProperties = {
  padding: "10px",
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  color: THEME.colors.neutral.light.textPrimary,
  verticalAlign: "middle",
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "11px 0",
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  fontSize: "14px",
  color: THEME.colors.neutral.light.textPrimary,
}

function Hint({ text, danger = false }: { text: string; danger?: boolean }) {
  return (
    <div
      style={{
        padding: "28px 0",
        textAlign: "center",
        fontSize: "13px",
        color: danger ? THEME.colors.semantic.danger : THEME.colors.neutral.light.textSecondary,
      }}
    >
      {text}
    </div>
  )
}

export default InsightsHealthView
